<template>
  <div class="expert-tool-print">
    <v-btn x-large text @click="print" class="ml-4">
      <eva-icon class="patient-create__icon mr-2" name="printer-outline" />
      {{ $ucfirst($t("dss.print")) }}
    </v-btn>
    <v-dialog width="600px" v-model="showDialog">
      <div class="dashboard-dialog-window">
        <div class="dashboard-dialog-window__message">
          {{ message }}
        </div>
        <div class="dashboard-dialog-window__actions">
          <v-btn
            x-large
            text
            class="ml-4"
            @click="showDialog = false"
            color="primary"
          >
            {{ $ucfirst($t("OK")) }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

/**
 * Component ExpertToolPrint.vue
 *
 * @module ExpertToolPrint
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.9] app/src/APP_DASHBOARD/templates/views/ExpertTool/ExpertToolPrint.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "ExpertToolPrint",
  data() {
    return {
      showDialog: false,
      message: "",
    };
  },
  computed: {
    ...mapGetters({
      previousRecommendation: "expertTool/previousRecommendation",
      isRecommendationSaved: "expertTool/isRecommendationSaved",
      safetyPrecautions: "expertTool/safetyPrecautions",
      studyId: "patientSummary/studyId",
      patientId: "patientSummary/id",
      prescriptionHistory: "expertTool/prescriptionHistory",
      hasPreviousPrescription: "expertTool/hasPreviousPrescription",
      recommendationUpdatedByAlgorithm:
        "expertTool/recommendationUpdatedByAlgorithm",
      recommendationReverted: "expertTool/recommendationReverted",
    }),
  },
  methods: {
    print() {
      if (this.$route.name === "PatientExpertToolSportsGoal") {
        if (!this.previousRecommendation?.id) {
          this.message = this.$ucfirst(
            this.$t("expertTool.printMessages.saveRecommendation")
          );
          this.showDialog = true;
          return;
        } else if (
          !this.isRecommendationSaved &&
          !this.recommendationUpdatedByAlgorithm &&
          !this.recommendationReverted
        ) {
          this.message = this.$ucfirst(
            this.$t("expertTool.printMessages.saveOrDiscard")
          );
          this.showDialog = true;
          return;
        }

        // this.printPrescription();
        this.backendPrintExerciseSubscription();
      } else if (this.$route.name === "PatientExpertToolSafetyPrecautions") {
        if (!this.previousRecommendation?.id) {
          this.message = this.$ucfirst(
            this.$t("expertTool.printMessages.saveRecommendation")
          );
          this.showDialog = true;
          return;
        }

        this.backendPrintSafetyPrecautions();
        // this.printSafetyPrecautions();
      } else if (this.$route.name === "PatientExpertToolHistory") {
        if (!this.hasPreviousPrescription) {
          this.message = this.$ucfirst(
            this.$t("expertTool.printMessages.noRecommendation")
          );
          this.showDialog = true;
          return;
        }

        this.printHistory();
      }
    },
    printPrescription() {
      const page = window.open(
        "",
        this.$t("general.physicalActivity", { catalog: "app_mobile" })
      );
      const style = this.getPrintStyles();

      page.document.title = this.$t("general.physicalActivity", {
        catalog: "app_mobile",
      });
      page.document.head.insertAdjacentHTML("beforeend", style);

      page.document.body.innerHTML = `
      <h2 style="padding-top: 20px">${this.studyId}</h2>
      <h4>${this.$t("general.physicalActivity", {
        catalog: "app_mobile",
      }).toUpperCase()}:</h4>
      <p>
        <span style="font-weight: bold;">
          ${this.$ucfirst(this.$t("expertTool.columnNames.intensity"))}:
        </span>
        <span>${this.previousRecommendation.intensityLabel}</span>
      </p>
      <p>
        <span style="font-weight: bold;">
          ${this.$ucfirst(this.$t("expertTool.columnNames.frequency"))}:
        </span>
        <span>${this.previousRecommendation.frequencyLabel}</span>
      </p>
      <p>
        <span style="font-weight: bold;">
          ${this.$ucfirst(this.$t("expertTool.columnNames.sessionDuration"))}:
        </span>
        <span>${this.previousRecommendation.sessionDurationLabel} ${this.$t(
        "general.minutes",
        { catalog: "app_mobile" }
      )}</span>
      </p>
      <p>
        <span style="font-weight: bold;">
          ${this.$ucfirst(this.$t("expertTool.columnNames.programDuration"))}:
        </span>
        <span>${this.previousRecommendation.programDurationLabel}</span>
      </p>
      <p>
        <span style="font-weight: bold;">
          ${this.$ucfirst(this.$t("pa.strengthTraining"))}:
        </span>
        <span>${this.previousRecommendation.strengthTrainingLabel}</span>
      </p>
      <p>
        <span style="font-weight: bold;">
          ${this.$ucfirst(
            this.$t("expertTool.columnNames.additionalTrainingStrategies")
          )}:
        </span>
        <br/>
        <span style="white-space: pre-line">${
          this.previousRecommendation.additionalTrainingStrategies
        }</span>
      </p>
      `;
      page.focus();
      page.print();
    },
    printSafetyPrecautions() {
      const page = window.open("", this.$t("expertTool.safetyPrecautions"));
      const style = this.getPrintStyles();

      page.document.title = this.$t("expertTool.safetyPrecautions");
      page.document.head.insertAdjacentHTML("beforeend", style);

      page.document.body.innerHTML = `
        <h2 style="padding-top: 20px">${this.studyId}</h2>
        <h4>${this.$t("expertTool.safetyPrecautions").toUpperCase()}:</h4>
          ${this.safetyPrecautions.reduce((accumulator, currentRule) => {
            return `
            ${accumulator}
            <p><span style="font-weight: bold;">${currentRule.name}</span></p>
            <ul>${currentRule.items.map((item) => `${item}`).join("")}</ul>
          `;
          }, "")}
      `;
      page.focus();
      page.print();
    },
    printHistory() {
      const page = window.open("", this.$t("expertTool.history"));

      page.document.head.innerHTML += `
      <style>
        td, th {
          padding-right: 10px;
          text-align: left;
          vertical-align: top;
        }
      </style>
      `;

      page.document.body.innerHTML = `
      <h2>${this.studyId}</h2>
      <h4>${this.$t("expertTool.history").toUpperCase()}:</h4>
      <table>
        <tbody>
          <tr>
            <th>${this.$ucfirst(this.$t("alerts.date"))}</th>
            <th>${this.$ucfirst(
              this.$t("expertTool.columnNames.intensity")
            )}</th>
            <th>${this.$ucfirst(
              this.$t("expertTool.columnNames.frequency")
            )}</th>
            <th>${this.$ucfirst(
              this.$t("expertTool.columnNames.sessionDuration")
            )}</th>
            <th>${this.$ucfirst(
              this.$t("expertTool.columnNames.programDuration")
            )}</th>
            <th>${this.$ucfirst(this.$t("pa.strengthTraining"))}</th>
          </tr>

          ${this.prescriptionHistory.reduce((accumulator, currentItem) => {
            return `${accumulator}<tr>
              <td>${moment(currentItem.createdAt).format("DD-MM-YYYY")}</td>
              <td>${currentItem.intensityLabel} HR: ${
              currentItem.heartRate
            }</td>
              <td>${currentItem.frequencyLabel}</td>
              <td>${currentItem.sessionDurationLabel}</td>
              <td>${currentItem.programDurationLabel}</td>
              <td>${currentItem.strengthTrainingLabel}</td>
            </tr>`;
          }, "")}
        </tbody>
      </table>
      `;
      page.focus();
      page.print();
    },
    getPrintStyles() {
      const firefox = navigator.userAgent.toLowerCase().indexOf("firefox") > -1;

      // hide header in firefox by setting small margin
      if (firefox) {
        return `
          <style media="print">
            @page {
              size: auto;
              margin: 3mm 10mm 10mm 10mm;
            }
          </style>
        `;
      }

      return `
          <style media="print">
            @page {
              size: auto;
              margin: 10mm;
            }
          </style>
        `;
    },
    async backendPrintExerciseSubscription() {
      return this.backendPrint('EXPERT_TOOL_LATEST_EXERCISE_PRESCRIPTION')
    },
    async backendPrintSafetyPrecautions() {
      return this.backendPrint('EXPERT_TOOL_LATEST_SAFETY_PRECAUTIONS')
    },
    async backendPrint(type) {
      const success =  await this.$app.patientManager.printPrescription(type, this.patientId);

      if (success) {
        if (this.$route.name === "PatientExpertToolSportsGoal") {
          this.$toasted.show(this.$ucfirst(this.$t('expertTool.printMessages.pdfExportedExercisePrescription')), {
            type : 'success',
            icon : 'check',
            position: 'bottom-center',
          });
        } else if (this.$route.name === "PatientExpertToolSafetyPrecautions") {
          this.$toasted.show(this.$ucfirst(this.$t('expertTool.printMessages.pdfExportedSafetyPrecautions')), {
            type : 'success',
            icon : 'check',
            position: 'bottom-center',
          });
        }
      }
    },
  },
};
</script>
